import { IAgentBlueprint } from "../types/base";
import {
  IPosProviderClover,
  IPosProviderFoodtec,
  IPosProviderKwick,
  IPosProviderLoman,
  IPosProviderSkytab,
  IPosProviderSpotOn,
  IPosProviderSquare,
  IPosProviderStream,
  IPosProviderToast,
  IPosProviderToastLegacy,
} from "../types/pos";
import {
  BaseAgentFormValues,
  CreateAgentFormValues,
  MenuConfigFormValues,
  PatchAgentFormValues,
} from "@/types/forms";
import { IMenuModuleConfig, Menu } from "@/types/menu";
import { ICustomPrepTimeHours, ICustomPrepTimeSlot } from "@/types/hours";
import { ReservationProvider } from "@/types/modules/reservation";
import { ImportAgentFormValues } from "@/schemas/forms/import";
import {
  defaultBaseOrderingModuleConfig,
  defaultDeliveryModuleConfig,
} from "@/defaults/modules/ordering";

/**
 * Helper to convert null values to undefined in custom prep time hours
 */
const normalizeCustomPrepTimeHours = (
  hours: unknown,
): ICustomPrepTimeHours | undefined => {
  if (!hours) return undefined;

  const normalizeSlot = (slot: {
    open: string;
    close: string;
    pickup?: string | null;
    delivery?: string | null;
  }): ICustomPrepTimeSlot => ({
    open: slot.open,
    close: slot.close,
    pickup: slot.pickup === null ? undefined : slot.pickup,
    delivery: slot.delivery === null ? undefined : slot.delivery,
  });

  const hoursData = hours as Record<
    string,
    Array<{
      open: string;
      close: string;
      pickup?: string | null;
      delivery?: string | null;
    }>
  >;

  return {
    sunday: hoursData.sunday?.map(normalizeSlot) || [],
    monday: hoursData.monday?.map(normalizeSlot) || [],
    tuesday: hoursData.tuesday?.map(normalizeSlot) || [],
    wednesday: hoursData.wednesday?.map(normalizeSlot) || [],
    thursday: hoursData.thursday?.map(normalizeSlot) || [],
    friday: hoursData.friday?.map(normalizeSlot) || [],
    saturday: hoursData.saturday?.map(normalizeSlot) || [],
  };
};

/**
 * Base function that converts IAgentBlueprint to common form values
 */
const convertAgentBlueprintToBaseFormValues = (
  agent: IAgentBlueprint,
): BaseAgentFormValues => {
  const result: BaseAgentFormValues = {
    id: agent.id,
    name: agent.name,
    type: agent.type,
    placeId: agent.placeId,
    timezone: agent.timezone,
    hours: agent.hours,
    hoursOrdering: agent.hoursOrdering ?? null,
    accountId: agent.accountId,

    twilioOverride: agent.twilioOverride
      ? {
          enabled: agent.twilioOverride.enabled || false,
          sid: agent.twilioOverride.sid,
          auth: agent.twilioOverride.auth,
        }
      : undefined,
    smsPhoneNumber: agent.smsPhoneNumber,

    greetingMessageOpen: agent.greetingMessageOpen,
    greetingMessageOrderingClosed: agent.greetingMessageOrderingClosed,
    greetingMessageClosed: agent.greetingMessageClosed,
    hubspotDealId: agent.hubspotDealId ?? undefined,
    prompt: agent.prompt,

    fallbackPhoneNumber: agent.fallbackPhoneNumber,
    transfers: agent.transfers,
    messages: agent.messages,

    twoPartyConsent: agent.twoPartyConsent,

    streamConnectedAt: agent.streamConnectedAt,

    pos: agent.pos,
    // Root-level prep times
    pickupPrepTime: agent.pickupPrepTime
      ? parseInt(agent.pickupPrepTime)
      : null,
    deliveryPrepTime: agent.deliveryPrepTime
      ? parseInt(agent.deliveryPrepTime)
      : null,
    customPrepTimeEnabled: agent.customPrepTimeEnabled,
    customPrepTimeHours: agent.customPrepTimeHours,
    holidayHours: agent.holidayHours ?? undefined,
  };

  // pos specific fields
  if (agent.pos === "toast-legacy") {
    const toastProvider = agent.posProvider as IPosProviderToastLegacy;
    result.toastUrl = toastProvider.url;
  } else if (agent.pos === "square") {
    const squareProvider = agent.posProvider as IPosProviderSquare;
    result.squareMerchantId = squareProvider.merchantId;
    result.squareLocationId = agent.posExternalId;
    result.squareAccessToken = squareProvider.accessToken;
    result.squareRefreshToken = squareProvider.refreshToken;
  } else if (agent.pos === "clover") {
    const cloverProvider = agent.posProvider as IPosProviderClover;
    result.cloverMerchantId = cloverProvider.merchantId;
    result.cloverAccessToken = cloverProvider.accessToken;
    result.cloverRefreshToken = cloverProvider.refreshToken;
    result.cloverOrderTypeId = cloverProvider.orderTypeId;
  } else if (agent.pos === "kwick") {
    const kwickProvider = agent.posProvider as IPosProviderKwick;
    result.kwickMerchantKey = kwickProvider.merchantKey;
    result.kwickMerchantToken = kwickProvider.merchantToken;
  } else if (agent.pos === "toast") {
    const toastProvider = agent.posProvider as IPosProviderToast;
    result.toastLocationId = agent.posExternalId;
    result.toastUrl = toastProvider.url || "";
    result.toastMenuGuids = toastProvider.menuGuids;
    result.toastPickupDiningOptionGuid =
      toastProvider.diningOptions?.PICKUP?.diningOptionGuid ?? null;
    result.toastPickupServiceCharges =
      toastProvider.diningOptions?.PICKUP?.serviceCharges ?? null;
    result.toastDeliveryDiningOptionGuid =
      toastProvider.diningOptions?.DELIVERY?.diningOptionGuid ?? null;
    result.toastDeliveryServiceCharges =
      toastProvider.diningOptions?.DELIVERY?.serviceCharges ?? null;
    result.toastRevenueCenterGuid = toastProvider.revenueCenterGuid;
    result.toastPaymentTypeGuid = toastProvider.paymentTypeGuid;
    result.toastPollStockUpdates = toastProvider.pollStockUpdates ?? false;
  } else if (agent.pos === "stream") {
    const streamProvider = agent.posProvider as IPosProviderStream;
    result.subprovider = streamProvider.subprovider ?? null;
    result.streamUrl = streamProvider.url || "";
    result.streamLocationId = agent.posExternalId;
  } else if (agent.pos === "spoton") {
    const spotOnProvider = agent.posProvider as IPosProviderSpotOn;
    result.spotOnLocationId = agent.posExternalId;
    result.spotOnMenuId = spotOnProvider.menuId;
  } else if (agent.pos === "skytab") {
    const skytabProvider = agent.posProvider as IPosProviderSkytab;
    result.skytabLocationId = agent.posExternalId;
    result.skytabOrderTypeRef = skytabProvider.orderTypeRef;
  } else if (agent.pos === "foodtec") {
    const foodtecProvider = agent.posProvider as IPosProviderFoodtec;
    result.foodtecBaseUrl = foodtecProvider.baseUrl;
    result.foodtecMenuAuth = foodtecProvider.menuAuth;
    result.foodtecCheckoutAuth = foodtecProvider.checkoutAuth;
    result.foodtecOrderAuth = foodtecProvider.orderAuth;
    result.foodtecOrderTypes = foodtecProvider.orderTypes;
  }

  // menu config
  if (agent.posProvider?.menuModuleConfig) {
    result.menuModuleConfig = convertMenuConfigToFormValues(
      agent.posProvider.menuModuleConfig,
    );
  }

  // ordering config
  if (agent.posProvider?.orderingModuleConfig) {
    const orderingModuleConfig = agent.posProvider.orderingModuleConfig;
    result.orderingOrderUntilClosed = orderingModuleConfig.orderUntilClosed;
    result.orderingAllowScheduledOrders =
      orderingModuleConfig.allowScheduledOrders;
    result.orderingShouldTotal = orderingModuleConfig.shouldTotal;
    result.orderingExactTimeEstimate = orderingModuleConfig.exactTimeEstimate;
    result.orderingInstantSubmit = orderingModuleConfig.instantSubmit;
    result.orderingMinLeadTime = orderingModuleConfig.minLeadTime;
    result.orderingMinTotalThreshold = orderingModuleConfig.minTotalThreshold;
    result.orderingMaxTotalThreshold = orderingModuleConfig.maxTotalThreshold;
    result.orderingPrepTime = orderingModuleConfig.prepTime;
    if (agent.posProvider.provider === "toast-legacy") {
      const toastOrderingModuleConfig = (
        agent.posProvider as IPosProviderToastLegacy
      ).orderingModuleConfig;
      result.orderingDynamicPrepTime =
        toastOrderingModuleConfig.dynamicPrepTime;
      result.orderingTaxRate = toastOrderingModuleConfig.taxRate;
    } else if (agent.posProvider.provider === "toast") {
      const toastOrderingModuleConfig = (agent.posProvider as IPosProviderToast)
        .orderingModuleConfig;
      result.orderingDynamicPrepTime =
        toastOrderingModuleConfig.dynamicPrepTime ?? false;
      result.useOrderEntryTool = toastOrderingModuleConfig.useOrderEntryTool;
    } else if (agent.posProvider.provider === "loman") {
      const lomanOrderingModuleConfig = (agent.posProvider as IPosProviderLoman)
        .orderingModuleConfig;
      result.orderingTaxRate = lomanOrderingModuleConfig.taxRate;
    } else if (agent.posProvider.provider === "stream") {
      const streamOrderingModuleConfig = (
        agent.posProvider as IPosProviderStream
      ).orderingModuleConfig;
      result.useOrderEntryTool = streamOrderingModuleConfig.useOrderEntryTool;
      result.orderingStreamTaxRate = streamOrderingModuleConfig.taxRate;
      if (agent.posProvider.subprovider === "toast") {
        result.orderingStreamDynamicPrepTime =
          streamOrderingModuleConfig.dynamicPrepTime;
      }
    } else if (agent.posProvider.provider === "foodtec") {
      const foodtecOrderingModuleConfig = (
        agent.posProvider as IPosProviderFoodtec
      ).orderingModuleConfig;
      result.useOrderEntryTool = foodtecOrderingModuleConfig.useOrderEntryTool;
    } else if (agent.posProvider.provider === "clover") {
      const cloverOrderingModuleConfig = (
        agent.posProvider as IPosProviderClover
      ).orderingModuleConfig;
      result.useOrderEntryTool = cloverOrderingModuleConfig.useOrderEntryTool;
    } else if (agent.posProvider.provider === "square") {
      const squareOrderingModuleConfig = (
        agent.posProvider as IPosProviderSquare
      ).orderingModuleConfig;
      result.useOrderEntryTool =
        squareOrderingModuleConfig.useOrderEntryTool ?? false;
    } else if (agent.posProvider.provider === "spoton") {
      const spotOnOrderingModuleConfig = (
        agent.posProvider as IPosProviderSpotOn
      ).orderingModuleConfig;
      result.useOrderEntryTool = spotOnOrderingModuleConfig.useOrderEntryTool;
    } else if (agent.posProvider.provider === "skytab") {
      const skytabOrderingModuleConfig = (
        agent.posProvider as IPosProviderSkytab
      ).orderingModuleConfig;
      result.useOrderEntryTool = skytabOrderingModuleConfig.useOrderEntryTool;
    }
  }

  // pickup config
  if (agent.posProvider?.orderingModuleConfig?.pickupModuleConfig) {
    const pickupModuleConfig =
      agent.posProvider.orderingModuleConfig.pickupModuleConfig;
    result.pickupEnabled = pickupModuleConfig.enabled;
    result.pickupPaymentModuleConfig = pickupModuleConfig.paymentModuleConfig;
    result.pickupMinTotalThreshold = pickupModuleConfig.minTotalThreshold;
    result.pickupMaxTotalThreshold = pickupModuleConfig.maxTotalThreshold;
  }

  // delivery config
  if (agent.posProvider?.orderingModuleConfig?.deliveryModuleConfig) {
    const deliveryModuleConfig =
      agent.posProvider.orderingModuleConfig.deliveryModuleConfig;
    result.deliveryEnabled = deliveryModuleConfig.enabled;
    result.deliveryVerifyAddress = deliveryModuleConfig.verifyAddress;
    result.deliveryLatitude = deliveryModuleConfig.latitude;
    result.deliveryLongitude = deliveryModuleConfig.longitude;
    // TODO: deprecate deliveryFee in favor of deliveryFeeConfig
    result.deliveryFee = deliveryModuleConfig.deliveryFee;
    // deliveryFeeConfig overrides the legacy flat deliveryFee field. If present,
    // extract the fee type and convert amounts from cents to dollars for form display.
    if (deliveryModuleConfig.deliveryFeeConfig) {
      result.deliveryFeeConfigType =
        deliveryModuleConfig.deliveryFeeConfig.type;
      if (deliveryModuleConfig.deliveryFeeConfig.type === "FLAT") {
        result.deliveryFee =
          deliveryModuleConfig.deliveryFeeConfig.amount / 100;
        result.deliveryFeeConfigTiers = [{ minMiles: 0, amount: 0 }];
      } else if (
        deliveryModuleConfig.deliveryFeeConfig.type === "MILEAGE_TIERED"
      ) {
        result.deliveryFeeConfigTiers =
          deliveryModuleConfig.deliveryFeeConfig.tiers.map((tier) => ({
            ...tier,
            amount: tier.amount / 100,
          }));
      }
    } else {
      result.deliveryFeeConfigType = "FLAT";
      result.deliveryFeeConfigTiers = [{ minMiles: 0, amount: 0 }];
    }
    result.deliveryRadius = deliveryModuleConfig.deliveryRadius;
    result.deliveryRegionCode = deliveryModuleConfig.regionCode;
    result.deliveryWaiveMinimum = deliveryModuleConfig.waiveDeliveryMinimum;
    result.deliveryFirstDeliveryMerchantId =
      deliveryModuleConfig.firstDeliveryMerchantId;
    result.deliveryFirstDeliveryCustomerApiKey =
      deliveryModuleConfig.firstDeliveryCustomerApiKey;
    result.deliveryPaymentModuleConfig =
      deliveryModuleConfig.paymentModuleConfig;
    result.deliveryMinTotalThreshold = deliveryModuleConfig.minTotalThreshold;
    result.deliveryMaxTotalThreshold = deliveryModuleConfig.maxTotalThreshold;
    result.firstDeliveryEnabled = deliveryModuleConfig.firstDeliveryEnabled;
  }

  if (agent.reservationProvider) {
    result.reservationProvider = agent.reservationProvider.provider;
    result.reservationEnabled =
      agent.reservationProvider.reservationModuleConfig?.enabled;
    result.reservationMaxPartyThreshold =
      agent.reservationProvider.reservationModuleConfig?.maxPartyThreshold;
    result.reservationPromptForSpecialRequest =
      agent.reservationProvider.reservationModuleConfig?.promptForSpecialRequest;
  }
  result.reservationExternalId = agent.reservationExternalId;

  if (agent.voicemailConfig) {
    result.voicemailEmail = agent.voicemailConfig?.email;
  }

  if (agent.streamConnectedAt) {
    result.streamConnectedAt = agent.streamConnectedAt;
  }

  return result;
};

/**
 * Converts IAgentBlueprint to CreateAgentFormValues
 */
export const convertAgentBlueprintToCreateFormValues = (
  agent: IAgentBlueprint,
): CreateAgentFormValues => {
  return {
    ...convertAgentBlueprintToBaseFormValues(agent),
    voiceId: null,
  };
};

/**
 * Converts IAgentBlueprint to PatchAgentFormValues
 */
export const convertAgentBlueprintToFormValues = (
  agent: IAgentBlueprint,
): PatchAgentFormValues => {
  return {
    ...convertAgentBlueprintToBaseFormValues(agent),
    slug: agent.slug,
    phoneNumber: agent.phoneNumber,
    hoursLoman: agent.hoursLoman,
    disallowClosedTransfers: agent.disallowClosedTransfers,
    faqs: agent.faqs || [],
    allowAnon: agent.allowAnon,
  };
};

/**
 * Converts CreateAgentFormValues to IAgentBlueprint
 */
export const convertCreateAgentFormValuesToAgentBlueprint = (
  formValues: CreateAgentFormValues | PatchAgentFormValues,
): IAgentBlueprint => {
  return convertFormValuesToAgentBlueprint(formValues, false);
};

/**
 * Converts PatchAgentFormValues to IAgentBlueprint
 * @param existingPosProvider - Optional existing posProvider to preserve fields not managed by the form (e.g. Toast's menuGuids, etc.)
 */
export const convertPatchAgentFormValuesToAgentBlueprint = (
  formValues: PatchAgentFormValues,
): IAgentBlueprint => {
  return convertFormValuesToAgentBlueprint(formValues, true);
};

const convertFormValuesToAgentBlueprint = (
  formValues: CreateAgentFormValues | PatchAgentFormValues,
  isPatch: boolean = false,
): IAgentBlueprint => {
  const blueprint: IAgentBlueprint = {
    id: formValues.id,
    accountId: formValues.accountId,
    fallbackPhoneNumber: formValues.fallbackPhoneNumber,
    greetingMessageOpen: formValues.greetingMessageOpen,
    greetingMessageOrderingClosed: formValues.greetingMessageOrderingClosed,
    greetingMessageClosed: formValues.greetingMessageClosed,
    hubspotDealId: formValues.hubspotDealId?.trim() || undefined,
    hours: formValues.hours,
    hoursOrdering: formValues.hoursOrdering,
    messages: formValues.messages,
    name: formValues.name,
    placeId: formValues.placeId,
    pos: formValues.pos,
    posProvider: null,
    prompt: formValues.prompt,
    smsPhoneNumber: formValues.smsPhoneNumber,
    timezone: formValues.timezone,
    transfers: formValues.transfers,
    twilioOverride: formValues.twilioOverride?.enabled
      ? {
          enabled: formValues.twilioOverride.enabled,
          sid: formValues.twilioOverride.sid,
          auth: formValues.twilioOverride.auth,
        }
      : undefined,
    twoPartyConsent: formValues.twoPartyConsent,
    type: formValues.type,
    // Convert numeric prep times to string for backend
    pickupPrepTime:
      formValues.pickupPrepTime !== null &&
      formValues.pickupPrepTime !== undefined
        ? String(formValues.pickupPrepTime)
        : null,
    deliveryPrepTime:
      formValues.deliveryPrepTime !== null &&
      formValues.deliveryPrepTime !== undefined
        ? String(formValues.deliveryPrepTime)
        : null,
    customPrepTimeEnabled: formValues.customPrepTimeEnabled,
    customPrepTimeHours: normalizeCustomPrepTimeHours(
      formValues.customPrepTimeHours,
    ),
    holidayHours: formValues.holidayHours as IAgentBlueprint["holidayHours"],
  };

  if (isPatch) {
    const patchFormValues = formValues as PatchAgentFormValues;
    blueprint.slug = patchFormValues.slug;
    blueprint.phoneNumber = patchFormValues.phoneNumber;
    blueprint.hoursLoman = patchFormValues.hoursLoman;
    blueprint.disallowClosedTransfers = patchFormValues.disallowClosedTransfers;
    blueprint.faqs = patchFormValues.faqs;
    blueprint.allowAnon = patchFormValues.allowAnon;
  }

  // pos provider
  if (formValues.pos) {
    const basePosProvider = {
      provider: formValues.pos,
      menuModuleConfig: formValues.menuModuleConfig
        ? convertFormValuesToMenuConfig(
            formValues.menuModuleConfig as MenuConfigFormValues,
          )
        : {
            enabled: true,
          },
      orderingModuleConfig: {
        orderUntilClosed: formValues.orderingOrderUntilClosed || false,
        allowScheduledOrders:
          formValues.orderingAllowScheduledOrders || "never",
        shouldTotal: formValues.orderingShouldTotal || false,
        exactTimeEstimate: formValues.orderingExactTimeEstimate || false,
        instantSubmit: formValues.orderingInstantSubmit || false,
        minLeadTime: formValues.orderingMinLeadTime,
        minTotalThreshold: formValues.orderingMinTotalThreshold,
        maxTotalThreshold: formValues.orderingMaxTotalThreshold,
        prepTime: formValues.orderingPrepTime,
        pickupModuleConfig: {
          enabled: formValues.pickupEnabled || false,
          paymentModuleConfig: formValues.pickupPaymentModuleConfig ?? {
            provider: "none",
          },
          minTotalThreshold: formValues.pickupMinTotalThreshold ?? undefined,
          maxTotalThreshold: formValues.pickupMaxTotalThreshold ?? undefined,
        },
        deliveryModuleConfig: {
          enabled: formValues.deliveryEnabled || false,
          paymentModuleConfig: formValues.deliveryPaymentModuleConfig ?? {
            provider: "none",
          },
          verifyAddress: formValues.deliveryVerifyAddress || false,
          latitude: formValues.deliveryLatitude || 0,
          longitude: formValues.deliveryLongitude || 0,
          deliveryFee: formValues.deliveryFee || 0,
          deliveryFeeConfig:
            formValues.deliveryFeeConfigType === "MILEAGE_TIERED"
              ? {
                  type: "MILEAGE_TIERED" as const,
                  // Use the existing tiers if present, otherwise seed with a
                  // default zero-mile/$0 tier. Amounts are stored as cents in
                  // the API, so multiply by 100 and round to avoid float drift.
                  tiers: (formValues.deliveryFeeConfigTiers?.length
                    ? formValues.deliveryFeeConfigTiers
                    : [{ minMiles: 0, amount: 0 }]
                  ).map((tier) => ({
                    ...tier,
                    amount: Math.round(tier.amount * 100),
                  })),
                }
              : {
                  type: "FLAT" as const,
                  amount: Math.round((formValues.deliveryFee || 0) * 100),
                },
          deliveryRadius: formValues.deliveryRadius || 0,
          regionCode: formValues.deliveryRegionCode || "US",
          waiveDeliveryMinimum: formValues.deliveryWaiveMinimum ?? undefined,
          minTotalThreshold: formValues.deliveryMinTotalThreshold ?? undefined,
          maxTotalThreshold: formValues.deliveryMaxTotalThreshold ?? undefined,
          firstDeliveryMerchantId:
            formValues.deliveryFirstDeliveryMerchantId || undefined,
          firstDeliveryEnabled: formValues.firstDeliveryEnabled || false,
          firstDeliveryCustomerApiKey:
            formValues.deliveryFirstDeliveryCustomerApiKey || undefined,
        },
      },
    };

    // pos specific fields
    switch (formValues.pos) {
      case "toast-legacy":
        blueprint.posProvider = {
          ...basePosProvider,
          url: formValues.toastUrl || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingTaxRate || 0,
                dynamicPrepTime: formValues.orderingDynamicPrepTime || false,
              }
            : undefined,
        } as IPosProviderToastLegacy;
        break;
      case "toast": {
        blueprint.posExternalId = formValues.toastLocationId;
        const buildDiningOptions = () => {
          const pickupGuid = formValues.toastPickupDiningOptionGuid ?? null;
          const pickupCharges = formValues.toastPickupServiceCharges ?? null;
          const deliveryGuid = formValues.toastDeliveryDiningOptionGuid ?? null;
          const deliveryCharges =
            formValues.toastDeliveryServiceCharges ?? null;

          return {
            PICKUP: pickupGuid
              ? {
                  diningOptionGuid: pickupGuid,
                  serviceCharges: pickupCharges ?? [],
                }
              : null,
            DELIVERY: deliveryGuid
              ? {
                  diningOptionGuid: deliveryGuid,
                  serviceCharges: deliveryCharges ?? [],
                }
              : null,
          };
        };
        blueprint.posProvider = {
          ...basePosProvider,
          url: formValues.toastUrl || undefined,
          menuGuids: formValues.toastMenuGuids ?? null,
          diningOptions: buildDiningOptions(),
          revenueCenterGuid: formValues.toastRevenueCenterGuid ?? null,
          paymentTypeGuid: formValues.toastPaymentTypeGuid ?? null,
          pollStockUpdates: formValues.toastPollStockUpdates ?? false,
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
                dynamicPrepTime: formValues.orderingDynamicPrepTime ?? false,
              }
            : undefined,
        } as IPosProviderToast;
        break;
      }
      case "square": {
        blueprint.posExternalId = formValues.squareLocationId;
        blueprint.posProvider = {
          ...basePosProvider,
          merchantId: formValues.squareMerchantId || "",
          accessToken: formValues.squareAccessToken || "",
          refreshToken: formValues.squareRefreshToken || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderSquare;
        break;
      }
      case "clover":
        blueprint.posProvider = {
          ...basePosProvider,
          merchantId: formValues.cloverMerchantId || "",
          accessToken: formValues.cloverAccessToken || "",
          refreshToken: formValues.cloverRefreshToken || "",
          orderTypeId: formValues.cloverOrderTypeId || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderClover;
        break;
      case "stream":
        blueprint.posExternalId = formValues.streamLocationId;
        blueprint.posProvider = {
          ...basePosProvider,
          subprovider: formValues.subprovider ?? undefined,
          url: formValues.streamUrl || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingStreamTaxRate || 0,
                useOrderEntryTool: formValues.useOrderEntryTool,
                ...(formValues.subprovider === "toast" &&
                  formValues.streamUrl && {
                    dynamicPrepTime:
                      formValues.orderingStreamDynamicPrepTime || false,
                  }),
              }
            : undefined,
        } as IPosProviderStream;
        break;
      case "kwick":
        blueprint.posProvider = {
          ...basePosProvider,
          merchantKey: formValues.kwickMerchantKey || "",
          merchantToken: formValues.kwickMerchantToken || "",
        } as IPosProviderKwick;
        break;
      case "loman":
        blueprint.posProvider = {
          ...basePosProvider,
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                taxRate: formValues.orderingTaxRate || 0,
              }
            : undefined,
        } as IPosProviderLoman;
        break;
      case "spoton": {
        blueprint.posExternalId = formValues.spotOnLocationId;
        const pickupPaymentConfig =
          basePosProvider.orderingModuleConfig?.pickupModuleConfig
            .paymentModuleConfig;
        const deliveryPaymentConfig =
          basePosProvider.orderingModuleConfig?.deliveryModuleConfig
            .paymentModuleConfig;
        const spotOnPickupPaymentConfig =
          pickupPaymentConfig?.provider === "none"
            ? {
                provider: "stripe",
                strategy: "required",
                requireTip: false,
              }
            : pickupPaymentConfig;
        const spotOnDeliveryPaymentConfig =
          deliveryPaymentConfig?.provider === "none"
            ? {
                provider: "stripe",
                strategy: "required",
                requireTip: false,
              }
            : deliveryPaymentConfig;
        blueprint.posProvider = {
          ...basePosProvider,
          menuId: formValues.spotOnMenuId || "",
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
                pickupModuleConfig: {
                  ...basePosProvider.orderingModuleConfig.pickupModuleConfig,
                  paymentModuleConfig: spotOnPickupPaymentConfig,
                },
                deliveryModuleConfig: {
                  ...basePosProvider.orderingModuleConfig.deliveryModuleConfig,
                  paymentModuleConfig: spotOnDeliveryPaymentConfig,
                },
              }
            : undefined,
        } as IPosProviderSpotOn;
        break;
      }
      case "skytab":
        blueprint.posExternalId = formValues.skytabLocationId;
        blueprint.posProvider = {
          ...basePosProvider,
          orderTypeRef: formValues.skytabOrderTypeRef || null,
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderSkytab;
        break;
      case "foodtec":
        blueprint.posProvider = {
          ...basePosProvider,
          baseUrl: formValues.foodtecBaseUrl || "",
          menuAuth: formValues.foodtecMenuAuth || "",
          checkoutAuth: formValues.foodtecCheckoutAuth || "",
          orderAuth: formValues.foodtecOrderAuth || "",
          orderTypes: formValues.foodtecOrderTypes || [],
          orderingModuleConfig: basePosProvider.orderingModuleConfig
            ? {
                ...basePosProvider.orderingModuleConfig,
                useOrderEntryTool: formValues.useOrderEntryTool,
              }
            : undefined,
        } as IPosProviderFoodtec;
        break;
    }
  }

  blueprint.reservationExternalId =
    formValues.reservationExternalId?.trim() || null;
  blueprint.reservationProvider =
    formValues.reservationEnabled === true
      ? {
          provider:
            (formValues.reservationProvider as ReservationProvider) ||
            "opentable",
          reservationModuleConfig: {
            enabled: true,
            maxPartyThreshold:
              formValues.reservationMaxPartyThreshold ?? undefined,
            promptForSpecialRequest:
              formValues.reservationPromptForSpecialRequest ?? undefined,
          },
        }
      : null;

  if (formValues.voicemailEmail) {
    blueprint.voicemailConfig = {
      email: formValues.voicemailEmail,
    };
  }

  return blueprint;
};

/**
 * Converts nested menu config to flattened structure for easier dirty field tracking
 */
export const convertMenuConfigToFormValues = (
  menuConfig: IMenuModuleConfig,
): MenuConfigFormValues => {
  const formValues: MenuConfigFormValues = {
    menuEnabled: menuConfig.enabled || false,
    menuLocked: menuConfig.locked ?? false,
    manualMenu: menuConfig.manualMenu,
    menuReplaceMap: menuConfig.replaceMap,
  };
  if (menuConfig.categoryBlacklist) {
    Object.entries(menuConfig.categoryBlacklist).forEach(
      ([categoryId, value]) => {
        formValues[`categoryBlacklist.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryNameOverrides) {
    Object.entries(menuConfig.categoryNameOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryNameOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryDescriptionOverrides) {
    Object.entries(menuConfig.categoryDescriptionOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryDescriptionOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryAvailabilityOverrides) {
    Object.entries(menuConfig.categoryAvailabilityOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryAvailabilityOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.categoryPrepTimeOverrides) {
    Object.entries(menuConfig.categoryPrepTimeOverrides).forEach(
      ([categoryId, value]) => {
        formValues[`categoryPrepTimeOverrides.${categoryId}`] = value;
      },
    );
  }
  if (menuConfig.itemBlacklist) {
    Object.entries(menuConfig.itemBlacklist).forEach(([itemId, value]) => {
      formValues[`itemBlacklist.${itemId}`] = value;
    });
  }
  if (menuConfig.itemNameOverrides) {
    Object.entries(menuConfig.itemNameOverrides).forEach(([itemId, value]) => {
      formValues[`itemNameOverrides.${itemId}`] = value;
    });
  }
  if (menuConfig.itemDescriptionOverrides) {
    Object.entries(menuConfig.itemDescriptionOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemDescriptionOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.itemAvailabilityOverrides) {
    Object.entries(menuConfig.itemAvailabilityOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemAvailabilityOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.itemPrepTimeOverrides) {
    Object.entries(menuConfig.itemPrepTimeOverrides).forEach(
      ([itemId, value]) => {
        formValues[`itemPrepTimeOverrides.${itemId}`] = value;
      },
    );
  }
  if (menuConfig.notesMap) {
    Object.entries(menuConfig.notesMap).forEach(([itemId, value]) => {
      formValues[`notesMap.${itemId}`] = value;
    });
  }
  if (menuConfig.modifierBlacklist) {
    Object.entries(menuConfig.modifierBlacklist).forEach(
      ([modifierId, value]) => {
        formValues[`modifierBlacklist.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.modifierNameOverrides) {
    Object.entries(menuConfig.modifierNameOverrides).forEach(
      ([modifierId, value]) => {
        formValues[`modifierNameOverrides.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.forceRequired) {
    Object.entries(menuConfig.forceRequired).forEach(([modifierId, value]) => {
      formValues[`forceRequired.${modifierId}`] = value;
    });
  }
  if (menuConfig.freeModifierCount) {
    Object.entries(menuConfig.freeModifierCount).forEach(
      ([modifierId, value]) => {
        formValues[`freeModifierCount.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.freeModifierCountV2) {
    Object.entries(menuConfig.freeModifierCountV2).forEach(
      ([itemId, modifierMap]) => {
        Object.entries(modifierMap).forEach(([modifierId, value]) => {
          formValues[`freeModifierCountV2.${itemId}.${modifierId}`] = value;
        });
      },
    );
  }
  if (menuConfig.allowStackedOverrides) {
    Object.entries(menuConfig.allowStackedOverrides).forEach(
      ([modifierId, value]) => {
        formValues[`allowStackedOverrides.${modifierId}`] = value;
      },
    );
  }
  if (menuConfig.optionBlacklist) {
    Object.entries(menuConfig.optionBlacklist).forEach(([optionId, value]) => {
      formValues[`optionBlacklist.${optionId}`] = value;
    });
  }
  if (menuConfig.optionNameOverrides) {
    Object.entries(menuConfig.optionNameOverrides).forEach(
      ([optionId, value]) => {
        formValues[`optionNameOverrides.${optionId}`] = value;
      },
    );
  }
  if (menuConfig.defaultOptionsOverrides) {
    Object.entries(menuConfig.defaultOptionsOverrides).forEach(
      ([itemId, optionMap]) => {
        Object.entries(optionMap).forEach(([optionId, value]) => {
          formValues[`defaultOptionsOverrides.${itemId}.${optionId}`] = value;
        });
      },
    );
  }
  return formValues;
};

/**
 * Converts flattened menu config back to nested structure for API calls
 */
export const convertFormValuesToMenuConfig = (
  menuFormValues: MenuConfigFormValues,
): IMenuModuleConfig => {
  const normalizedConfig = { ...menuFormValues };
  if (
    menuFormValues.categoryNameOverrides &&
    typeof menuFormValues.categoryNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryNameOverrides;
  }
  if (
    menuFormValues.categoryBlacklist &&
    typeof menuFormValues.categoryBlacklist === "object"
  ) {
    Object.entries(menuFormValues.categoryBlacklist).forEach(([id, value]) => {
      normalizedConfig[`categoryBlacklist.${id}`] = value;
    });
    delete normalizedConfig.categoryBlacklist;
  }
  if (
    menuFormValues.categoryDescriptionOverrides &&
    typeof menuFormValues.categoryDescriptionOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryDescriptionOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryDescriptionOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryDescriptionOverrides;
  }
  if (
    menuFormValues.categoryAvailabilityOverrides &&
    typeof menuFormValues.categoryAvailabilityOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryAvailabilityOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryAvailabilityOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryAvailabilityOverrides;
  }
  if (
    menuFormValues.categoryPrepTimeOverrides &&
    typeof menuFormValues.categoryPrepTimeOverrides === "object"
  ) {
    Object.entries(menuFormValues.categoryPrepTimeOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`categoryPrepTimeOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.categoryPrepTimeOverrides;
  }
  if (
    menuFormValues.itemBlacklist &&
    typeof menuFormValues.itemBlacklist === "object"
  ) {
    Object.entries(menuFormValues.itemBlacklist).forEach(([id, value]) => {
      normalizedConfig[`itemBlacklist.${id}`] = value;
    });
    delete normalizedConfig.itemBlacklist;
  }
  if (
    menuFormValues.itemNameOverrides &&
    typeof menuFormValues.itemNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemNameOverrides).forEach(([id, value]) => {
      normalizedConfig[`itemNameOverrides.${id}`] = value;
    });
    delete normalizedConfig.itemNameOverrides;
  }
  if (
    menuFormValues.itemDescriptionOverrides &&
    typeof menuFormValues.itemDescriptionOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemDescriptionOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemDescriptionOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemDescriptionOverrides;
  }
  if (
    menuFormValues.itemAvailabilityOverrides &&
    typeof menuFormValues.itemAvailabilityOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemAvailabilityOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemAvailabilityOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemAvailabilityOverrides;
  }
  if (
    menuFormValues.itemPrepTimeOverrides &&
    typeof menuFormValues.itemPrepTimeOverrides === "object"
  ) {
    Object.entries(menuFormValues.itemPrepTimeOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`itemPrepTimeOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.itemPrepTimeOverrides;
  }
  if (menuFormValues.notesMap && typeof menuFormValues.notesMap === "object") {
    Object.entries(menuFormValues.notesMap).forEach(([id, value]) => {
      normalizedConfig[`notesMap.${id}`] = value;
    });
    delete normalizedConfig.notesMap;
  }

  if (
    menuFormValues.modifierBlacklist &&
    typeof menuFormValues.modifierBlacklist === "object"
  ) {
    Object.entries(menuFormValues.modifierBlacklist).forEach(([id, value]) => {
      normalizedConfig[`modifierBlacklist.${id}`] = value;
    });
    delete normalizedConfig.modifierBlacklist;
  }

  if (
    menuFormValues.modifierNameOverrides &&
    typeof menuFormValues.modifierNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.modifierNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`modifierNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.modifierNameOverrides;
  }

  if (
    menuFormValues.forceRequired &&
    typeof menuFormValues.forceRequired === "object"
  ) {
    Object.entries(menuFormValues.forceRequired).forEach(([id, value]) => {
      normalizedConfig[`forceRequired.${id}`] = value;
    });
    delete normalizedConfig.forceRequired;
  }

  if (
    menuFormValues.freeModifierCount &&
    typeof menuFormValues.freeModifierCount === "object"
  ) {
    Object.entries(menuFormValues.freeModifierCount).forEach(([id, value]) => {
      normalizedConfig[`freeModifierCount.${id}`] = value;
    });
    delete normalizedConfig.freeModifierCount;
  }

  if (
    menuFormValues.freeModifierCountV2 &&
    typeof menuFormValues.freeModifierCountV2 === "object"
  ) {
    Object.entries(menuFormValues.freeModifierCountV2).forEach(
      ([itemId, modifierMap]) => {
        if (typeof modifierMap === "object" && modifierMap !== null) {
          Object.entries(modifierMap).forEach(([modifierId, value]) => {
            normalizedConfig[`freeModifierCountV2.${itemId}.${modifierId}`] =
              value;
          });
        }
      },
    );
    delete normalizedConfig.freeModifierCountV2;
  }

  if (
    menuFormValues.allowStackedOverrides &&
    typeof menuFormValues.allowStackedOverrides === "object"
  ) {
    Object.entries(menuFormValues.allowStackedOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`allowStackedOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.allowStackedOverrides;
  }

  if (
    menuFormValues.optionBlacklist &&
    typeof menuFormValues.optionBlacklist === "object"
  ) {
    Object.entries(menuFormValues.optionBlacklist).forEach(([id, value]) => {
      normalizedConfig[`optionBlacklist.${id}`] = value;
    });
    delete normalizedConfig.optionBlacklist;
  }

  if (
    menuFormValues.optionNameOverrides &&
    typeof menuFormValues.optionNameOverrides === "object"
  ) {
    Object.entries(menuFormValues.optionNameOverrides).forEach(
      ([id, value]) => {
        normalizedConfig[`optionNameOverrides.${id}`] = value;
      },
    );
    delete normalizedConfig.optionNameOverrides;
  }

  if (
    menuFormValues.defaultOptionsOverrides &&
    typeof menuFormValues.defaultOptionsOverrides === "object"
  ) {
    Object.entries(menuFormValues.defaultOptionsOverrides).forEach(
      ([itemId, optionMap]) => {
        if (typeof optionMap === "object" && optionMap !== null) {
          Object.entries(optionMap).forEach(([optionId, value]) => {
            normalizedConfig[`defaultOptionsOverrides.${itemId}.${optionId}`] =
              value;
          });
        }
      },
    );
    delete normalizedConfig.defaultOptionsOverrides;
  }
  const nested: IMenuModuleConfig & {
    [key: string]: unknown;
  } = {
    enabled: normalizedConfig.menuEnabled || false,
    locked: normalizedConfig.menuLocked ?? false,
    manualMenu: normalizedConfig.manualMenu as Menu | undefined,
    replaceMap: normalizedConfig.menuReplaceMap,
  };

  const overrides: Record<string, Record<string, unknown>> = {
    categoryBlacklist: {},
    categoryNameOverrides: {},
    categoryDescriptionOverrides: {},
    categoryAvailabilityOverrides: {},
    categoryPrepTimeOverrides: {},
    itemBlacklist: {},
    itemNameOverrides: {},
    itemDescriptionOverrides: {},
    itemAvailabilityOverrides: {},
    itemPrepTimeOverrides: {},
    notesMap: {},
    modifierBlacklist: {},
    modifierNameOverrides: {},
    forceRequired: {},
    freeModifierCount: {},
    allowStackedOverrides: {},
    optionBlacklist: {},
    optionNameOverrides: {},
  };

  // Single pass through all flattened fields
  Object.entries(normalizedConfig).forEach(([key, value]) => {
    if (value === undefined) return;

    // Handle defaultOptionsOverrides (special case with nested structure)
    if (key.startsWith("defaultOptionsOverrides.")) {
      const parts = key.substring("defaultOptionsOverrides.".length).split(".");
      if (parts.length === 2) {
        const [itemId, optionId] = parts;
        if (!nested.defaultOptionsOverrides) {
          nested.defaultOptionsOverrides = {};
        }
        const defaultOptionsOverrides =
          nested.defaultOptionsOverrides as Record<
            string,
            Record<string, unknown>
          >;
        if (!defaultOptionsOverrides[itemId]) {
          defaultOptionsOverrides[itemId] = {};
        }
        defaultOptionsOverrides[itemId][optionId] = value;
      }
      return;
    }
    if (key.startsWith("freeModifierCountV2.")) {
      const parts = key.substring("freeModifierCountV2.".length).split(".");
      if (parts.length === 2) {
        const [itemId, modifierId] = parts;
        if (!nested.freeModifierCountV2) {
          nested.freeModifierCountV2 = {};
        }
        const freeModifierCountV2 = nested.freeModifierCountV2 as Record<
          string,
          Record<string, unknown>
        >;
        if (!freeModifierCountV2[itemId]) {
          freeModifierCountV2[itemId] = {};
        }
        freeModifierCountV2[itemId][modifierId] = value;
      }
      return;
    }

    // Handle all other override types
    for (const [overrideType, overrideObj] of Object.entries(overrides)) {
      if (key.startsWith(`${overrideType}.`)) {
        const id = key.substring(overrideType.length + 1);
        overrideObj[id] = value;
        break;
      }
    }
  });

  // Only add non-empty override objects to the nested structure
  Object.entries(overrides).forEach(([overrideType, overrideObj]) => {
    if (Object.keys(overrideObj).length > 0) {
      nested[overrideType] = overrideObj;
    }
  });

  return nested;
};

/**
 * Helper to convert payment string to PaymentModuleConfig
 */
const getPaymentModuleConfig = (
  payment?: "required" | "enabled" | "disabled",
  paymentType?: "pickup" | "delivery",
) => {
  if (payment === "required" || payment === "enabled") {
    return {
      provider: "stripe" as const,
      strategy: "required" as const,
      requireTip: paymentType === "delivery",
    };
  }
  return { provider: "none" as const };
};

// POS providers that use the order entry tool (from defaultPosProviders)
const posProvidersWithOrderEntryTool = [
  "toast",
  "square",
  "clover",
  "stream",
  "spoton",
  "skytab",
  "foodtec",
];

/**
 * Transforms ImportAgentFormValues to CreateAgentFormValues shape
 * so we can reuse convertFormValuesToAgentBlueprint
 */
const transformImportFormToCreateForm = (
  formValues: ImportAgentFormValues,
): CreateAgentFormValues => {
  // Generate default greeting messages based on name
  const greetingMessageOpen = `Hi this is ${formValues.name}. How can I help you?`;
  const greetingMessageClosed = `Thanks for calling ${formValues.name}. Please note that we are currently closed. How can I help you today?`;
  const greetingMessageOrderingClosed = `Thanks for calling ${formValues.name}. Please note that our kitchen is currently closed. How can I help you today?`;

  // Determine effective POS - if streamRequired and compatible POS, use stream with subprovider
  // Handle "none" as null for the output
  const inputPos = formValues.pos === "none" ? null : formValues.pos;
  const shouldUseStream = formValues.streamRequired || inputPos === "stream";

  const effectivePos = shouldUseStream ? "stream" : inputPos;
  const subprovider = shouldUseStream
    ? inputPos &&
      ["toast", "clover", "square", "skytab", "ncr_aloha_essentials"].includes(
        inputPos,
      )
      ? (inputPos as
          | "toast"
          | "clover"
          | "square"
          | "skytab"
          | "ncr_aloha_essentials")
      : undefined
    : undefined;

  const pickupEnabled = formValues.type?.includes("PICKUP") ?? false;
  const deliveryEnabled = formValues.type?.includes("DELIVERY") ?? false;
  const pickupPaymentModuleConfig = getPaymentModuleConfig(
    formValues.pickupPayment,
    "pickup",
  );
  const deliveryPaymentModuleConfig = getPaymentModuleConfig(
    formValues.deliveryPayment,
    "delivery",
  );

  // Determine where to put the online ordering URL based on POS
  const toastUrl =
    effectivePos === "toast-legacy"
      ? formValues.onlineOrderingUrl || ""
      : undefined;
  const streamUrl =
    effectivePos === "stream" ? formValues.onlineOrderingUrl || "" : undefined;

  // useOrderEntryTool is true for specific POS providers (from defaultPosProviders)
  const useOrderEntryTool =
    effectivePos && posProvidersWithOrderEntryTool.includes(effectivePos)
      ? true
      : undefined;

  return {
    // Core fields from form
    name: formValues.name,
    type: formValues.type,
    placeId: formValues.placeId,
    accountId: formValues.accountId,
    timezone: formValues.timezone,
    hours: formValues.hours ?? null,

    // Greeting messages (generated)
    greetingMessageOpen,
    greetingMessageClosed,
    greetingMessageOrderingClosed,

    // Action triggers from form
    fallbackPhoneNumber: formValues.fallbackPhoneNumber ?? null,
    messages: [],

    // Consent from form (detected from state)
    twoPartyConsent: formValues.twoPartyConsent ?? null,

    // POS configuration
    pos: effectivePos,
    subprovider,
    toastUrl,
    streamUrl,

    // POS-specific ordering config (from defaultPosProviders)
    // toast-legacy defaults dynamicPrepTime to true, toast direct to false
    orderingDynamicPrepTime:
      effectivePos === "toast-legacy"
        ? true
        : effectivePos === "toast"
          ? false
          : undefined,
    // Stream with toast subprovider can have dynamicPrepTime
    orderingStreamDynamicPrepTime:
      effectivePos === "stream" && subprovider === "toast" ? false : undefined,
    // useOrderEntryTool for square, clover, stream, spoton, foodtec
    useOrderEntryTool,

    // Base ordering config (from defaultBaseOrderingModuleConfig)
    orderingOrderUntilClosed:
      defaultBaseOrderingModuleConfig.orderingOrderUntilClosed,
    orderingAllowScheduledOrders:
      defaultBaseOrderingModuleConfig.orderingAllowScheduledOrders,
    orderingShouldTotal: defaultBaseOrderingModuleConfig.orderingShouldTotal,
    orderingExactTimeEstimate:
      defaultBaseOrderingModuleConfig.orderingExactTimeEstimate,

    // Pickup configuration (enabled/payment from form)
    pickupEnabled,
    pickupPaymentModuleConfig,

    // Delivery configuration (enabled/payment from form, rest from defaultDeliveryModuleConfig)
    deliveryEnabled,
    deliveryPaymentModuleConfig,
    deliveryVerifyAddress: defaultDeliveryModuleConfig.deliveryVerifyAddress,
    deliveryLatitude: defaultDeliveryModuleConfig.deliveryLatitude,
    deliveryLongitude: defaultDeliveryModuleConfig.deliveryLongitude,
    deliveryFee: defaultDeliveryModuleConfig.deliveryFee,
    deliveryFeeConfigType: defaultDeliveryModuleConfig.deliveryFeeConfigType,
    deliveryFeeConfigTiers: defaultDeliveryModuleConfig.deliveryFeeConfigTiers,
    deliveryRadius: defaultDeliveryModuleConfig.deliveryRadius,
    deliveryRegionCode: defaultDeliveryModuleConfig.deliveryRegionCode,

    reservationEnabled: formValues.type?.includes("RESERVATION") ?? false,

    // Required for CreateAgentFormValues
    voiceId: null,
  };
};

/**
 * Converts ImportAgentFormValues to IAgentBlueprint
 */
export const convertImportFormValuesToAgentBlueprint = (
  formValues: ImportAgentFormValues,
): IAgentBlueprint => {
  const createFormValues = transformImportFormToCreateForm(formValues);
  return convertFormValuesToAgentBlueprint(createFormValues, false);
};
